import React, { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../services/api';
import {
    Paper, Typography, Box, Switch, FormControlLabel, Alert, IconButton, TextField, Skeleton, Tooltip,
    Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy, faCheck, faRotate } from '@awesome.me/kit-e6334c76d4/icons/duotone/solid';
import { Navigate } from 'react-router-dom';
import { isCurrentUserSiteAdmin } from './UserManagementPage';

interface ResponseConfig {
    pollingEnabled: boolean;
    webhookUrl: string;
}

interface AppRelease {
    available: boolean;
    versionCode?: number;
    versionName?: string;
    notes?: string;
    sha256?: string;
    size?: number;
    releasedAt?: string;
}

const sha256Hex = async (file: File): Promise<string> => {
    const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
    return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, '0')).join('');
};

const EnvironmentConfigPage: React.FC = () => {
    const [config, setConfig] = useState<ResponseConfig | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [isRotateDialogOpen, setIsRotateDialogOpen] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [rotated, setRotated] = useState(false);
    const [isChecking, setIsChecking] = useState(false);
    const [checkResult, setCheckResult] = useState<string | null>(null);
    const [release, setRelease] = useState<AppRelease | null>(null);
    const [apkFile, setApkFile] = useState<File | null>(null);
    const [versionCode, setVersionCode] = useState('');
    const [versionName, setVersionName] = useState('');
    const [releaseNotes, setReleaseNotes] = useState('');
    const [publishState, setPublishState] = useState<'idle' | 'hashing' | 'uploading' | 'publishing'>('idle');
    const [publishMessage, setPublishMessage] = useState<string | null>(null);
    const isSiteAdmin = isCurrentUserSiteAdmin();

    const fetchConfig = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await apiFetch('/api/risc/license/config/response');
            if (!response.ok) throw new Error('Failed to load environment configuration.');
            setConfig(await response.json());
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const fetchRelease = useCallback(async () => {
        try {
            const response = await apiFetch('/api/risc/app/update');
            if (response.ok) setRelease(await response.json());
        } catch {
            // non-fatal; the section shows "no release" until it loads
        }
    }, []);

    useEffect(() => {
        if (isSiteAdmin) {
            fetchConfig();
            fetchRelease();
        }
    }, [fetchConfig, fetchRelease, isSiteAdmin]);

    const handlePublish = async () => {
        if (!apkFile || !versionCode || !versionName) return;
        setError(null);
        setPublishMessage(null);
        try {
            setPublishState('hashing');
            const sha256 = await sha256Hex(apkFile);

            setPublishState('uploading');
            const urlResp = await apiFetch('/api/risc/app/upload-url', {
                method: 'POST',
                body: JSON.stringify({ fileName: apkFile.name }),
            });
            if (!urlResp.ok) throw new Error('Failed to prepare the upload.');
            const { uploadUrl, s3Key } = await urlResp.json();

            const put = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/vnd.android.package-archive' },
                body: apkFile,
            });
            if (!put.ok) throw new Error('APK upload failed.');

            setPublishState('publishing');
            const pub = await apiFetch('/api/risc/app/release', {
                method: 'POST',
                body: JSON.stringify({
                    versionCode: parseInt(versionCode, 10),
                    versionName,
                    notes: releaseNotes || undefined,
                    s3Key,
                    sha256,
                }),
            });
            if (!pub.ok) throw new Error((await pub.json().catch(() => null))?.error || 'Failed to publish the release.');
            const result = await pub.json();
            setPublishMessage(result.message);
            setApkFile(null);
            setVersionCode('');
            setVersionName('');
            setReleaseNotes('');
            await fetchRelease();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setPublishState('idle');
        }
    };

    if (!isSiteAdmin) {
        return <Navigate to="/" />;
    }

    const handleTogglePolling = async (enabled: boolean) => {
        if (!config) return;
        setIsSaving(true);
        setError(null);
        const previous = config.pollingEnabled;
        setConfig({ ...config, pollingEnabled: enabled });
        try {
            const response = await apiFetch('/api/risc/license/config/response', {
                method: 'PUT',
                body: JSON.stringify({ pollingEnabled: enabled }),
            });
            if (!response.ok) throw new Error('Failed to update response polling.');
        } catch (err) {
            setConfig({ ...config, pollingEnabled: previous });
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleCheckNow = async () => {
        if (!config?.webhookUrl) return;
        setIsChecking(true);
        setCheckResult(null);
        setError(null);
        try {
            // Fires the same public webhook the RISC backend uses (plain
            // fetch: the token in the URL is the authentication).
            const response = await fetch(config.webhookUrl, { method: 'POST' });
            const data = await response.json().catch(() => null);
            if (!response.ok) throw new Error(data?.error || 'Webhook call failed.');
            setCheckResult(data?.locked
                ? 'A mailbox check is already in progress — any waiting responses are being processed.'
                : 'Mailbox checked. Any waiting responses have been processed.');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsChecking(false);
        }
    };

    const handleRotate = async () => {
        setIsRotating(true);
        setError(null);
        setRotated(false);
        try {
            const response = await apiFetch('/api/risc/license/config/response/rotate', { method: 'POST' });
            if (!response.ok) throw new Error('Failed to rotate the webhook URL.');
            const data = await response.json();
            setConfig(prev => prev ? { ...prev, webhookUrl: data.webhookUrl } : prev);
            setRotated(true);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred.');
        } finally {
            setIsRotating(false);
            setIsRotateDialogOpen(false);
        }
    };

    const handleCopy = async () => {
        if (!config) return;
        try {
            await navigator.clipboard.writeText(config.webhookUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            setError('Unable to copy to clipboard.');
        }
    };

    return (
        <Paper sx={{ p: 3, m: 1, maxWidth: 900 }}>
            <Typography variant="h6" gutterBottom>Environment Configuration</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box mt={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Response Polling</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    When enabled, RapID actively polls the response mailbox after each submission and
                    sweeps it on a schedule. Disable only once the RISC backend reliably calls the
                    response webhook below — with polling off, responses are collected exclusively when
                    the webhook is notified.
                </Typography>
                {isLoading ? (
                    <Skeleton width={220} height={38} />
                ) : (
                    <FormControlLabel
                        control={
                            <Switch
                                checked={config?.pollingEnabled ?? true}
                                onChange={(e) => handleTogglePolling(e.target.checked)}
                                disabled={isSaving}
                            />
                        }
                        label={config?.pollingEnabled ? 'Polling enabled' : 'Polling disabled'}
                    />
                )}
                {!isLoading && config && !config.pollingEnabled && (
                    <Alert severity="warning" sx={{ mt: 1 }}>
                        Polling is disabled. Responses are only collected when the RISC backend calls
                        the webhook. If responses stop arriving, re-enable polling.
                    </Alert>
                )}
            </Box>

            <Box mt={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Response Notification Webhook</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Provide this URL to the RISC backend team. An HTTP <code>POST</code> to it signals
                    that a response is ready, and RapID checks the response mailbox immediately. The
                    webhook works whether polling is enabled or not. Treat the URL as a secret — the
                    token in the path is the only authentication.
                </Typography>
                {rotated && (
                    <Alert severity="success" sx={{ mb: 1 }}>
                        Webhook URL rotated. Update the RISC backend with the new URL — the previous
                        one no longer works.
                    </Alert>
                )}
                {isLoading ? (
                    <Skeleton height={56} />
                ) : (
                    <>
                        <Box display="flex" alignItems="center" gap={1}>
                            <TextField
                                value={config?.webhookUrl ?? ''}
                                fullWidth
                                size="small"
                                InputProps={{ readOnly: true, sx: { fontFamily: 'var(--font-mono)', fontSize: '0.85rem' } }}
                            />
                            <Tooltip title={copied ? 'Copied' : 'Copy webhook URL'}>
                                <IconButton onClick={handleCopy} color={copied ? 'success' : 'default'}>
                                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} swapOpacity />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box display="flex" gap={1.5} mt={1.5} alignItems="center">
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleCheckNow}
                                disabled={isChecking}
                            >
                                {isChecking ? 'Checking…' : 'Check for responses now'}
                            </Button>
                            <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                startIcon={<FontAwesomeIcon icon={faRotate} swapOpacity />}
                                onClick={() => setIsRotateDialogOpen(true)}
                                disabled={isRotating}
                            >
                                Rotate URL
                            </Button>
                        </Box>
                        {checkResult && <Alert severity="success" sx={{ mt: 1.5 }}>{checkResult}</Alert>}
                    </>
                )}
            </Box>

            <Box mt={4}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>App Releases</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    The current RapidID Android release for this environment. Publishing a new APK
                    notifies provisioned devices over MQTT; the app also checks on every login.
                </Typography>

                {release?.available ? (
                    <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                        Current release: <strong>{release.versionName}</strong> (code {release.versionCode})
                        {release.releasedAt && <> · published {new Date(release.releasedAt).toLocaleString()}</>}
                        {typeof release.size === 'number' && <> · {(release.size / (1024 * 1024)).toFixed(1)} MB</>}
                        {release.notes && <><br />{release.notes}</>}
                    </Alert>
                ) : (
                    <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
                        No release published yet. Devices have nothing to update to.
                    </Alert>
                )}

                <Box display="flex" flexDirection="column" gap={1.5} maxWidth={480}>
                    <Button variant="outlined" component="label" size="small" sx={{ alignSelf: 'flex-start' }}>
                        {apkFile ? apkFile.name : 'Choose APK…'}
                        <input
                            hidden
                            type="file"
                            accept=".apk,application/vnd.android.package-archive"
                            onChange={(e) => setApkFile(e.target.files?.[0] ?? null)}
                        />
                    </Button>
                    <Box display="flex" gap={1.5}>
                        <TextField
                            label="Version code"
                            size="small"
                            value={versionCode}
                            onChange={(e) => setVersionCode(e.target.value.replace(/\D/g, ''))}
                            sx={{ width: 140 }}
                        />
                        <TextField
                            label="Version name"
                            size="small"
                            placeholder="e.g. 1.4.2"
                            value={versionName}
                            onChange={(e) => setVersionName(e.target.value)}
                            sx={{ flexGrow: 1 }}
                        />
                    </Box>
                    <TextField
                        label="Release notes (optional)"
                        size="small"
                        multiline
                        minRows={2}
                        value={releaseNotes}
                        onChange={(e) => setReleaseNotes(e.target.value)}
                    />
                    <Button
                        variant="contained"
                        sx={{ alignSelf: 'flex-start' }}
                        disabled={!apkFile || !versionCode || !versionName || publishState !== 'idle'}
                        onClick={handlePublish}
                    >
                        {publishState === 'idle' && 'Publish release'}
                        {publishState === 'hashing' && 'Verifying APK…'}
                        {publishState === 'uploading' && 'Uploading…'}
                        {publishState === 'publishing' && 'Publishing…'}
                    </Button>
                    {publishMessage && <Alert severity="success">{publishMessage}</Alert>}
                </Box>
            </Box>

            <Dialog open={isRotateDialogOpen} onClose={() => !isRotating && setIsRotateDialogOpen(false)}>
                <DialogTitle>Rotate the webhook URL?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Rotating generates a new secret URL and <strong>immediately invalidates the
                        current one</strong>. The RISC backend will not be able to notify RapID of
                        pending responses until it is updated with the new URL.
                        {config && !config.pollingEnabled && (
                            <> Response polling is currently <strong>disabled</strong>, so responses
                            will stop arriving entirely until the backend is updated.</>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setIsRotateDialogOpen(false)} disabled={isRotating}>Cancel</Button>
                    <Button onClick={handleRotate} color="warning" variant="contained" disabled={isRotating}>
                        {isRotating ? 'Rotating…' : 'Rotate URL'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    );
};

export default EnvironmentConfigPage;
